/* eslint-disable no-console */
import { BaseElement } from '@/common/elements/BaseElement'
import { load } from '@/pdr/load'
import { PublicApi } from './PublicApi'

const STRATSYS_PUBLIC_RUNTIME_API_ROOT = 'STRATSYS_PUBLIC_RUNTIME_API_ROOT'
const validCustomElementRegex = /^[a-z][a-z0-9-]+[0-9a-z]$/

class StratsysPublicRuntimeCustomElement extends BaseElement {
  connectedCallback() {
    const attributes = this.#getAttributes()
    const runtimeElements = [...document.querySelectorAll('stratsys-public-runtime')]
    if (runtimeElements.length) {
      const currentAttributes = runtimeElements.map(this.#getAttributes)

      const haveSameClientId = currentAttributes.every(
        (attr) => attr.clientId === attributes.clientId
      )

      const haveSameCompany = currentAttributes.every((attr) => attr.company === attributes.company)

      const haveSameTenantId = currentAttributes.every(
        (attr) => attr.tenantId === attributes.tenantId
      )

      if (!haveSameCompany || !haveSameClientId || !haveSameTenantId) {
        return this.#initFailed()
      }
    }
    this.#init(attributes)
  }

  #init(attributes) {
    this.#initPdr(attributes)
    this.#loadApps(attributes)
  }

  #loadApps = (attributes) => {
    requestAnimationFrame(() => {
      const createApp = (name, version) => ({ name, version: version || 'latest' })
      const childApps = [...this.children].map((c) =>
        createApp(c.nodeName.toLowerCase(), c.getAttribute('public-app-version'))
      )
      if (childApps.length) {
        this.classList.add('with-children')
      }
      const attributeApps = attributes.apps.map((app) => createApp(...app.split('@')))
      console.log(attributeApps)
      const apps = [...attributeApps, ...childApps].filter((a) =>
        validCustomElementRegex.test(a.name)
      )
      apps.forEach(({ version, name }) => {
        console.log(`[stratsys-public-runtime] Lazy loading version "${version}" of ${name}...`)
        window.PDR.mfe.load({ tag: name, version: version, required: false })
      })
    })
  }

  #getAttributes = () => {
    const tenantId = this.optionalAttribute('tenant-id')
    const company = this.requiredAttribute('company')
    const clientId = this.requiredAttribute('client-id')
    const idp = this.optionalAttribute('idp', 'stratsys.com')
    const lang = this.optionalAttribute('lang', this.lang() || 'sv')

    const url = /^https?:\/\//i.test(idp)
      ? idp
      : `https://login.svc.${idp}/${tenantId || company}/connect/token`

    const apps = this.getArrayAttribute('apps')
    const scopes = this.getArrayAttribute('scopes')

    return {
      clientId,
      company,
      tenantId,
      url,
      lang,
      apps,
      scopes
    }
  }

  #initFailed = () => {
    while (this.firstChild) {
      this.removeChild(this.lastChild)
    }
    this.classList.add('sem1', 'failed')
    const message = document.createElement('div')
    message.classList.add('sem-visual', 'sem-separate', 'sem-indent', 'sem-caution')
    const p = document.createElement('p')
    p.textContent =
      lang === 'sv'
        ? 'Kunde inte starta en till Stratsy-miljö eftersom en miljö redan är startad med andra iställningar.'
        : 'Could not initiate a Stratsys runtime as another runtime has already been registered with other settings.'
    message.appendChild(p)
    this.appendChild(message)
  }

  #initPdr = (attributes) => {
    window[STRATSYS_PUBLIC_RUNTIME_API_ROOT] =
      window[STRATSYS_PUBLIC_RUNTIME_API_ROOT] || new PublicApi(attributes)
    console.log('[stratsys-public-runtime] Initializing PDR runtime...')
    window.PDR.init({
      env: 'webView',
      webView: {
        apiRoot: STRATSYS_PUBLIC_RUNTIME_API_ROOT,
        getUser: 'getUser'
      },
      http: {
        loginAndRetryCodes: [401, 403],
        extraHeaders: {
          'Stratsys-CompanyCode': attributes.company
        }
      },
      microfrontends: {
        baseUrl: 'https://microfrontends.svc.stratsys.com/{package}/{version}',
        suppressWarnings: true,
        mode: 'dynamic'
      },
      logging: {
        verbose: false
      },
      lang: attributes.lang
    })
  }
}

if (!customElements.get('stratsys-public-runtime')) {
  load(() => {}, false).then(() => {
    customElements.define('stratsys-public-runtime', StratsysPublicRuntimeCustomElement)
  })
}
